import {
  useUpdateAdditionalEmail,
  useGetUserImageDownloadSignedUrlQuery,
} from '@exam-room/graphql';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, generatePath } from 'react-router-dom';

import { Card, LoadingSpinner, UserProfileAvatar } from '../../../components';
import { ProfileConfirmationForm } from '../../../containers';
import { useGetExamEventSettings } from '../../../hooks';
import { useAuth } from '../../../providers';
import { PATHS } from '../../../routes';

interface ProfileConfirmationInput {
  otherEmail?: string;
  confirmOtherEmail?: string;
  isAdditionalEmailEnabled: boolean;
  isConfirmationEmailEnabled: boolean;
}

function ProfileConfirmation() {
  const { t } = useTranslation();
  const { examCardId } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();

  const [updateAdditionalEmail, { loading: isUpdateAdditionalEmail }] = useUpdateAdditionalEmail();
  const { data: userSignedUrlsData, loading: userImageSignedUrlLoading } =
    useGetUserImageDownloadSignedUrlQuery({ skip: !user?.avatarImageUrl });

  const { isLoading: isGetResultSettingsLoading, settings } = useGetExamEventSettings({
    cardId: examCardId as string,
  });

  const userAvatarImageUrl = user?.avatarImageUrl;
  const userImageUrl =
    userAvatarImageUrl && typeof userAvatarImageUrl === 'string'
      ? userSignedUrlsData?.cluGetUserImageDownloadSignedUrl.signedUrls[userAvatarImageUrl]
      : undefined;

  const redirectToInstructionPage = () => {
    if (examCardId) {
      const instructionPath = generatePath(PATHS.Exam.Instruction, {
        examCardId,
      });
      navigate(instructionPath);
    }
  };

  const handleOnUpdateAdditionalEmail = async (data: ProfileConfirmationInput) => {
    const { otherEmail, isAdditionalEmailEnabled } = data;

    if (!isAdditionalEmailEnabled || !otherEmail) {
      return redirectToInstructionPage();
    }

    await updateAdditionalEmail({
      variables: {
        examCardId: examCardId as string,
        email: otherEmail,
      },
      onCompleted: () => {
        redirectToInstructionPage();
      },
      onError: (error) => {
        console.error('Error accepting privacy policy:', error);
      },
    });
  };

  const examResultSettings = settings.result;
  const hasCertificate = examResultSettings.hasCertificate;

  return (
    <div className='flex size-full flex-col overflow-auto'>
      <div className='flex-grow p-6 text-center'>
        <Card className='mx-auto flex size-full h-max max-w-[800px] flex-col items-center gap-6 p-10'>
          {isGetResultSettingsLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <UserProfileAvatar imageUrl={userImageUrl} isLoading={userImageSignedUrlLoading} />
              <div className='grid gap-2'>
                <h3>{user?.displayName}</h3>
                {!hasCertificate ? <p>{user?.email}</p> : null}
              </div>
              <div className='grid gap-3'>
                {hasCertificate ? (
                  <p className='text-body1 text-text-primary'>
                    {t('profileConfirmation.email.profileConfirmationDescription')}{' '}
                  </p>
                ) : null}
                <ProfileConfirmationForm
                  onSubmit={handleOnUpdateAdditionalEmail}
                  isLoading={isUpdateAdditionalEmail}
                  registerEmail={user?.email || ''}
                  options={{
                    shouldDisplayEmailField: hasCertificate,
                  }}
                />
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
const ProfileConfirmationRoute = {
  path: PATHS.Exam.ProfileConfirmation,
  element: <ProfileConfirmation />,
};

export default ProfileConfirmationRoute;
